import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import AppHeader from '@/components/common/AppHeader'
import AppFooter from '@/components/common/AppFooter'
import { useUI } from '@/stores/ui'
import { contactApi } from '@/api/endpoints/contact'

export default function ContactUsPage() {
  const addToast = useUI((state) => state.addToast)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await contactApi.submit({
        name,
        email,
        subject,
        message,
      })
      addToast({
        message: 'Thank you for your message. We will get back to you as soon as possible.',
        type: 'success',
      })
      // Clear form
      setName('')
      setEmail('')
      setSubject('')
      setMessage('')
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to send message. Please try again.',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8"
        >
          <div className="mb-4">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to Login
            </Link>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h1>

          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h2 className="font-semibold text-yellow-900 mb-2">Tech Support Policy</h2>
            <p className="text-sm text-yellow-800">
              This is a non-urgent service, and we're a (very) small organization. We'll do our best to serve your tech support needs, but please be patient! We appreciate your understanding.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Your Name *</label>
              <input
                type="text"
                className="input-field w-full focus-ring"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Your Email *</label>
              <input
                type="email"
                className="input-field w-full focus-ring"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Subject *</label>
              <input
                type="text"
                className="input-field w-full focus-ring"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Message *</label>
              <textarea
                className="input-field w-full min-h-[200px] focus-ring"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                placeholder="Please describe your question or issue..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Link to="/login" className="btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn-primary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-4">
              <strong>Parishes and Administrators:</strong> For subscription or billing inquiries, please contact us through your account dashboard or use the form above.
            </p>
            <p className="text-sm text-gray-600">
              For questions about our Privacy Policy or Terms of Service, please see our <Link to="/privacy" className="text-yellow-600 hover:text-yellow-700">Privacy Policy page</Link>.
            </p>
          </div>
        </motion.div>
      </main>
      <AppFooter />
    </div>
  )
}

